import "./styles.css";
import { mountSplashScreen } from "./ui/SplashScreen.mjs";

import {
  TILE_TYPES,
  TILE_COLORS,
  RESOURCE_TYPES,
  RESOURCE_EMOJI,
  TILE_EMOJI,
} from "./gdd-types/tileTypes.mjs";
import { generateContiguousTile } from "./gdd-types/tileModel.mjs";
import {
  hexToPixel,
  hexKey,
  getNeighbor,
  oppositeSide,
  getEffectiveSide,
  pixelToHex,
  hexesWithinRadius,
  hexDistance,
} from "./math/hexMath.mjs";
import {
  HexaworldsRenderer,
  SPRITE_OFFSET_Y,
} from "./renderer/HexaworldsRenderer.mjs";
import { drawBlueprintTile } from "./renderer/blueprint/blueprintDraw.mjs";
import { SpriteAtlas } from "./renderer/assets/SpriteAtlas.mjs";
import {
  buildSpriteIndex,
  isWaterTile,
  isWaterOverlay,
  computeOverlayPlacementReward,
  OVERLAY_ECONOMY,
  getOverlayFamily,
} from "./renderer/assets/spriteIndex.mjs";
import tilesJson from "./assets/tiles/tiles.json";
import tilesImageUrl from "url:./assets/tiles/tiles.png";
import { AudioManager } from "./audio/AudioManager.mjs";
import { MUSIC_TRACKS } from "./audio/audioManifest.mjs";
import { AmbienceManager } from "./audio/ambience/AmbienceManager.mjs";
import { resolveBiome } from "./audio/ambience/BiomeResolver.mjs";
import { CloudSystem } from "./renderer/clouds/CloudSystem.mjs";
import { CloudRenderer } from "./renderer/clouds/CloudRenderer.mjs";
import { CloudShadowRenderer } from "./renderer/clouds/CloudShadowRenderer.mjs";
import { ZoomTracker } from "./renderer/ZoomTracker.mjs";
import { WorldClock } from "./world/WorldClock.mjs";
import { PostFx } from "./renderer/postfx/PostFx.mjs";
import { WeatherState } from "./world/weather/WeatherState.mjs";
import { PrecipRenderer } from "./renderer/weather/PrecipRenderer.mjs";
import { BUILD_NUMBER } from "./buildInfo.mjs";
import { Analytics } from "./analytics/Analytics.mjs";
import { getOrCreatePlayerId, setGaUserId } from "./analytics/playerId.mjs";
import { tileSignatureFromSides } from "./analytics/tileSignature.mjs";
import { QuestManager } from "./quests/QuestManager.mjs";
import { QuestUI } from "./quests/QuestUI.mjs";
import {
  rebuildRenderMetaAround,
  buildAllRenderMeta,
  clearAllRenderMeta,
} from "./renderer/adjacency/AdjacencyTransitions.mjs";

// ===== CONSTANTS =====
const HEX_SIZE = 40;
const OVERLAY_UNLOCK_CADENCE = 100; // every N stars, unlock 1 regular overlay (v2)

// ===== AUDIO =====
const audio = new AudioManager();
const ambienceManager = new AmbienceManager(audio);

// Save-data check: default ambience off on metered connections
if (navigator.connection && navigator.connection.saveData) {
  ambienceManager.setEnabled(false);
}

function getBiomeResolveDeps() {
  return {
    tiles,
    hexKey,
    pixelToHex,
    hexSize: HEX_SIZE,
    getMajorityType,
    hexesWithinRadius,
  };
}

function resolveAndUpdateAmbience(force) {
  if (!ambienceManager.isReady) return;
  const resolved = resolveBiome(
    { cameraX: camera.x, cameraY: camera.y, zoom: camera.zoom },
    getBiomeResolveDeps()
  );
  if (force) {
    ambienceManager.forceUpdateFromResolvedBiome(resolved);
  } else {
    ambienceManager.updateFromResolvedBiome(resolved);
  }
}

function unlockAudio() {
  if (audio.isUnlocked()) return;
  audio.unlock().then(() => {
    audio.preloadSfx();
    audio.preloadMusic([MUSIC_TRACKS[0].id]).then(() => {
      audio.setPlaylist(
        MUSIC_TRACKS.map((t) => t.id),
        { shuffle: false }
      );
      audio.playMusic();
    });
    // Start ambience
    ambienceManager.unlock();
    resolveAndUpdateAmbience(true);
  });
}

// Unlock on first user interaction
const unlockEvents = ["click", "keydown", "touchstart"];
function onFirstInteraction() {
  unlockAudio();
  for (const ev of unlockEvents) {
    window.removeEventListener(ev, onFirstInteraction, true);
  }
}
for (const ev of unlockEvents) {
  window.addEventListener(ev, onFirstInteraction, true);
}

// ===== GAME STATE =====
let tiles = {};
let resources = {};
let score = { stars: 0 };
let hand = [];
let selectedHandIndex = 0;
let camera = { x: 0, y: 0, zoom: 1 };
let animations = [];
let placementAnims = [];
let pendingUnlockNotifs = [];

// Overlay state
let overlayUnlocks = {
  locked: [],
  unlocked: [],
  starsSinceLastUnlock: 0,
};
let overlayIdCounter = 0;

// Ability state
let rerollChainCount = 0;
let storageCapacity = 0;
let storage = [null, null, null];
let demolishMode = false;
let selectedStorageIndex = -1;

// ===== ANALYTICS HELPERS =====
const _SLOT_NAMES = ["optimal", "explorer", "comfort"];

function _starsBucket(n) {
  return n <= 5 ? String(n) : "6+";
}

function _adjBucket(n) {
  return n <= 3 ? String(n) : "4+";
}

function _chainBucket(n) {
  return n <= 0 ? "0" : n === 1 ? "1" : "2+";
}

let _abilityUnlockTracked = { preview: false, reroll: false, storage: false };

function checkAbilityUnlocks() {
  if (!_abilityUnlockTracked.preview && score.stars >= 25) {
    _abilityUnlockTracked.preview = true;
    Analytics.track("ability_unlock", { build: String(BUILD_NUMBER), ability: "preview", stars: 25 });
  }
  if (!_abilityUnlockTracked.reroll && score.stars >= 50) {
    _abilityUnlockTracked.reroll = true;
    Analytics.track("ability_unlock", { build: String(BUILD_NUMBER), ability: "reroll", stars: 50 });
  }
  if (!_abilityUnlockTracked.storage && score.stars >= 100) {
    _abilityUnlockTracked.storage = true;
    Analytics.track("ability_unlock", { build: String(BUILD_NUMBER), ability: "storage", stars: 100 });
  }
}

function initAbilityUnlockTracking() {
  _abilityUnlockTracked = {
    preview: score.stars >= 25,
    reroll: score.stars >= 50,
    storage: score.stars >= 100,
  };
}

// ===== UNLOCK CHECKS =====
function isPreviewUnlocked() {
  return score.stars >= 25;
}
function isRerollUnlocked() {
  return score.stars >= 50;
}
function isStorageUnlocked() {
  return score.stars >= 100;
}

// ===== RESOURCE HELPERS =====
function initResources() {
  resources = {};
  for (const res of RESOURCE_TYPES) {
    resources[res] = 0;
  }
}
initResources();

function canAfford(cost) {
  for (const [res, amount] of Object.entries(cost)) {
    if ((resources[res] || 0) < amount) return false;
  }
  return true;
}

function payCost(cost) {
  for (const [res, amount] of Object.entries(cost)) {
    resources[res] = (resources[res] || 0) - amount;
  }
}

function getRerollCost(n) {
  if (n <= 0) return { fish: 1, food: 1, supplies: 1 };
  if (n === 1) return { fish: 2, food: 2, supplies: 1 };
  return { fish: 3, food: 3, supplies: 2 };
}

function getStorageUpgradeCost(cap) {
  if (cap === 1) return { wood: 6, stone: 4, supplies: 2 };
  return { wood: 10, stone: 8, supplies: 4, crystals: 2 };
}

function getDemolishCost(tile) {
  if (tile.overlaySpriteKey) return { stars: 3 };
  return { stars: 5 };
}

function formatCost(cost) {
  return Object.entries(cost)
    .map(([res, amt]) => {
      if (res === "stars") return `${amt}\u2b50`;
      return `${amt}${RESOURCE_EMOJI[res]}`;
    })
    .join(" ");
}

/** Return the most common type in a sides array. */
function getMajorityType(sides) {
  const counts = {};
  for (const s of sides) {
    counts[s] = (counts[s] || 0) + 1;
  }
  let best = null,
    bestCount = 0;
  for (const [type, count] of Object.entries(counts)) {
    if (count > bestCount) {
      best = type;
      bestCount = count;
    }
  }
  return best;
}

// ===== ATLAS STATE (loaded async) =====
let spriteAtlas = null;
let spriteIndex = null;

// ===== RENDERER SETUP =====
const lifeCanvas = document.getElementById("life-canvas");
const canvas = document.getElementById("game");
const ctx = canvas.getContext("2d");
const cloudsCanvas = document.getElementById("clouds");
const cloudsCtx = cloudsCanvas.getContext("2d");
const postfxCanvas = document.getElementById("postfx");
const postfxCtx = postfxCanvas.getContext("2d");

const cloudSystem = new CloudSystem();
const cloudRenderer = new CloudRenderer();
const cloudShadowRenderer = new CloudShadowRenderer();
const zoomTracker = new ZoomTracker();
const worldClock = new WorldClock();
const postFx = new PostFx();
const weatherState = new WeatherState(42);
const precipRenderer = new PrecipRenderer();
cloudSystem.weatherState = weatherState;

const renderer = new HexaworldsRenderer({
  lifeCanvas,
  blueprintCanvas: canvas,
  hexSize: HEX_SIZE,
  worldSeed: 42,
  cloudShadowRenderer,
});

let _lastFrameTime = 0;

function resize() {
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
  lifeCanvas.width = window.innerWidth;
  lifeCanvas.height = window.innerHeight;
  cloudsCanvas.width = window.innerWidth;
  cloudsCanvas.height = window.innerHeight;
  postfxCanvas.width = window.innerWidth;
  postfxCanvas.height = window.innerHeight;
}
window.addEventListener("resize", resize);
resize();

// ===== TILE GENERATION =====
let tileIdCounter = 0;

/** Count how many exposed edges exist per type (edges facing empty neighbours). */
function getExposedTypeCounts() {
  const counts = new Map();
  for (const key of Object.keys(tiles)) {
    const t = tiles[key];
    for (let s = 0; s < 6; s++) {
      const nb = getNeighbor(t.coord.q, t.coord.r, s);
      if (!tiles[hexKey(nb.q, nb.r)]) {
        const type = getEffectiveSide(t.sides, t.rotation, s);
        counts.set(type, (counts.get(type) || 0) + 1);
      }
    }
  }
  return counts;
}

/** Collect all distinct types present across all placed tile sides. */
function getPlacedTypes() {
  const placed = new Set();
  for (const key of Object.keys(tiles)) {
    const t = tiles[key];
    for (const side of t.sides) placed.add(side);
  }
  return placed;
}

/** Build a tile object from a sides array, applying random rotation offset. */
function buildTile(sides) {
  const offset = Math.floor(Math.random() * 6);
  const rotatedSides = [];
  for (let i = 0; i < 6; i++) {
    rotatedSides.push(sides[(((i - offset) % 6) + 6) % 6]);
  }
  const tile = {
    id: "tile_" + tileIdCounter++,
    coord: null,
    rotation: 0,
    sides: rotatedSides,
    placed: false,
    placedAtMs: null,
    seed: Math.floor(Math.random() * 0x7fffffff),
  };
  renderer.assignSpriteKeys(tile);
  return tile;
}

/** Get the top N most-exposed types sorted by count descending. */
function getTopExposed(exposedCounts, n) {
  return [...exposedCounts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map((e) => e[0]);
}

/** Sample a type from exposed counts using sqrt-weighted random selection. */
function pickWeightedType(exposedCounts, excludeType) {
  const entries = [...exposedCounts.entries()].filter(
    ([type]) => type !== excludeType
  );
  if (entries.length === 0) return null;
  let total = 0;
  const weights = entries.map(([type, count]) => {
    const w = Math.sqrt(count);
    total += w;
    return { type, w };
  });
  let r = Math.random() * total;
  for (const { type, w } of weights) {
    r -= w;
    if (r <= 0) return type;
  }
  return weights[weights.length - 1].type;
}

/** Pick two distinct types via weighted random sampling. */
function pickWeightedTypePair(exposedCounts) {
  const typeA = pickWeightedType(exposedCounts);
  if (typeA === null) return [null, null];
  const typeB = pickWeightedType(exposedCounts, typeA);
  return [typeA, typeB];
}

/** Slot 1 — "Optimal" tile: best match potential using weighted-random types. */
function generateOptimalTile(exposedCounts) {
  const [typeA, typeB] = pickWeightedTypePair(exposedCounts);
  if (typeA === null) return generateContiguousTileFallback();
  if (typeB === null) {
    return buildTile(Array(6).fill(typeA));
  }
  const roll = Math.random();
  const aCount = roll < 0.15 ? 3 : roll < 0.55 ? 4 : 5;
  const sides = [];
  for (let i = 0; i < 6; i++) {
    sides.push(i < aCount ? typeA : typeB);
  }
  return buildTile(sides);
}

/** Slot 2 — "Explorer" tile: introduces new types paired with weighted-random anchor. */
function generateExplorerTile(exposedCounts, placedTypes) {
  const anchor = pickWeightedType(exposedCounts);
  if (anchor === null) return generateContiguousTileFallback();

  const unused = TILE_TYPES.filter((t) => !placedTypes.has(t));
  let newType;
  if (unused.length > 0) {
    newType = unused[Math.floor(Math.random() * unused.length)];
  } else {
    const sorted = [...exposedCounts.entries()].sort((a, b) => a[1] - b[1]);
    newType = sorted[0][0];
    if (newType === anchor && sorted.length > 1) newType = sorted[1][0];
  }

  const sides = [];
  for (let i = 0; i < 6; i++) {
    sides.push(i < 3 ? anchor : newType);
  }
  return buildTile(sides);
}

/** Slot 3 — "Comfort" tile: safe mono tile of a weighted-random exposed type. */
function generateComfortTile(exposedCounts) {
  const pick = pickWeightedType(exposedCounts);
  if (pick === null) return generateContiguousTileFallback();
  return buildTile(Array(6).fill(pick));
}

/** Fallback: fully random tile via existing generator. */
function generateContiguousTileFallback() {
  const exposedCounts = getExposedTypeCounts();
  const exposed = new Set(exposedCounts.keys());
  const tile = generateContiguousTile(tileIdCounter++, {
    exposedTypes: exposed.size > 0 ? exposed : null,
  });
  renderer.assignSpriteKeys(tile);
  return tile;
}

/** Classify a hand item as 'overlay', 'full', or 'two-type'. */
function classifyHandItem(item) {
  if (item.isOverlay) return "overlay";
  const types = new Set(item.sides);
  return types.size <= 1 ? "full" : "two-type";
}

/** Generate a tile guaranteed to have exactly 2 distinct types. */
function generateTwoTypeTile(exposedCounts, placedTypes) {
  if (exposedCounts.size === 0) return generateContiguousTileFallback();

  if (Math.random() < 0.5) {
    // Optimal-style: 4:2 or 5:1 split using weighted-random types
    const [typeA, typeB_] = pickWeightedTypePair(exposedCounts);
    if (typeA === null) return generateContiguousTileFallback();
    let typeB = typeB_;
    if (typeB === null) {
      // Only 1 exposed type — pick a random different type
      const others = TILE_TYPES.filter((t) => t !== typeA);
      typeB = others[Math.floor(Math.random() * others.length)];
    }
    const roll = Math.random();
    const aCount = roll < 0.15 ? 3 : roll < 0.55 ? 4 : 5;
    const sides = [];
    for (let i = 0; i < 6; i++) {
      sides.push(i < aCount ? typeA : typeB);
    }
    return buildTile(sides);
  } else {
    // Explorer-style: 3:3 split
    return generateExplorerTile(exposedCounts, placedTypes);
  }
}

/** Create an overlay hand item from a random overlay in the given list. */
function generateOverlayHandItem(placeableKeys) {
  const idx = Math.floor(Math.random() * placeableKeys.length);
  const overlayKey = placeableKeys[idx];
  return {
    isOverlay: true,
    overlayKey,
    id: "overlay_" + overlayIdCounter++,
    sides: [],
  };
}

/** Generate a tile for a specific hand slot using gap-filling strategy. */
function generateTileForSlot(slot) {
  const exposedCounts = getExposedTypeCounts();
  const placedTypes = getPlacedTypes();

  // Scan existing hand to see what categories are present
  const present = new Set();
  for (const item of hand) {
    if (item) present.add(classifyHandItem(item));
  }

  // Ideal set of categories
  const ideal = ["two-type", "full", "overlay"];
  const missing = ideal.filter((c) => !present.has(c));

  // Pick category: 80% fill a gap, 20% wild random
  let isRandomFallback = false;
  let category;
  if (missing.length > 0 && Math.random() < 0.8) {
    category = missing[Math.floor(Math.random() * missing.length)];
  } else {
    // Wild: pick from all three weighted toward tiles
    isRandomFallback = true;
    const roll = Math.random();
    category = roll < 0.4 ? "two-type" : roll < 0.8 ? "full" : "overlay";
  }

  // Try to generate overlay if chosen
  if (category === "overlay" && overlayUnlocks.unlocked.length > 0) {
    const { hasVacantWater, hasVacantGround } = getVacantTileTypes();
    const placeableKeys = overlayUnlocks.unlocked.filter((k) =>
      isWaterOverlay(k) ? hasVacantWater : hasVacantGround
    );
    if (placeableKeys.length > 0) {
      return generateOverlayHandItem(placeableKeys);
    }
    // Fall through to a tile if can't generate overlay
    category = Math.random() < 0.5 ? "two-type" : "full";
  }

  let result;
  if (exposedCounts.size === 0) {
    result = generateContiguousTileFallback();
    isRandomFallback = true;
  } else if (category === "two-type") {
    result = generateTwoTypeTile(exposedCounts, placedTypes);
  } else {
    result = generateComfortTile(exposedCounts);
  }

  Analytics.track("tile_offer", {
    build: String(BUILD_NUMBER),
    slot: _SLOT_NAMES[slot] || "slot" + slot,
    tile_sig: tileSignatureFromSides(result.sides),
    is_random_fallback: isRandomFallback,
  });

  return result;
}

function fillHand() {
  while (hand.length < 3) {
    const slot = hand.length;
    hand.push(generateTileForSlot(slot));
  }
  if (selectedHandIndex >= hand.length) selectedHandIndex = 0;
  renderHand();
}

// ===== PLACEMENT =====
function getValidPlacements() {
  const tileKeys = Object.keys(tiles);
  if (tileKeys.length === 0) return new Set([hexKey(0, 0)]);
  const valid = new Set();
  for (const key of tileKeys) {
    const t = tiles[key];
    for (let s = 0; s < 6; s++) {
      const nb = getNeighbor(t.coord.q, t.coord.r, s);
      const nk = hexKey(nb.q, nb.r);
      if (!tiles[nk]) valid.add(nk);
    }
  }
  return valid;
}

function placeTile(q, r) {
  if (tiles[hexKey(q, r)]) return false;
  const tile = hand[selectedHandIndex];

  Analytics.startSessionIfNeeded({ build: String(BUILD_NUMBER), first_action: "tile_place" });

  tile.coord = { q, r };
  tile.placed = true;
  tile.placedAtMs = performance.now();
  tiles[hexKey(q, r)] = tile;

  if (!tile.baseSpriteKey) renderer.assignSpriteKeys(tile);

  const px = hexToPixel(q, r, HEX_SIZE);
  placementAnims.push({
    x: px.x,
    y: px.y,
    t: performance.now(),
    duration: 300,
  });

  renderer.upsertTile(tile);

  // Only count matching sides for stars — no resource gains from tile placement
  let totalStars = 0;
  for (let s = 0; s < 6; s++) {
    const nb = getNeighbor(q, r, s);
    const neighbor = tiles[hexKey(nb.q, nb.r)];
    if (!neighbor) continue;
    const mySideType = getEffectiveSide(tile.sides, tile.rotation, s);
    const theirSideType = getEffectiveSide(
      neighbor.sides,
      neighbor.rotation,
      oppositeSide(s)
    );
    if (mySideType === theirSideType) {
      totalStars++;
    }
  }

  if (totalStars > 0) {
    score.stars += totalStars;
    overlayUnlocks.starsSinceLastUnlock += totalStars;
    checkOverlayUnlock();
    animations.push({
      x: px.x,
      y: px.y - 10,
      text: "\u2b50 +" + totalStars,
      startTime: performance.now(),
      duration: 1000,
      isScore: true,
    });
  }

  checkAbilityUnlocks();
  Analytics.track("tile_place", {
    build: String(BUILD_NUMBER),
    source: _SLOT_NAMES[selectedHandIndex] || "slot" + selectedHandIndex,
    tile_sig: tileSignatureFromSides(tile.sides),
    stars_bucket: _starsBucket(totalStars),
  });

  // Ensure storage unlocks when threshold met
  if (isStorageUnlocked() && storageCapacity < 1) {
    storageCapacity = 1;
  }

  rerollChainCount = 0;

  audio.playSfx("tile_place");

  hand.splice(selectedHandIndex, 1);
  fillHand();
  updateHUD();
  renderStoragePanel();
  saveGame();

  rebuildAdjacencyAt({ q, r });

  // Quest v2: build placement event
  {
    const majorType = getMajorityType(tile.sides);
    let neighbourCount = 0;
    const neighbourTerrainTypes = new Set();
    for (let s = 0; s < 6; s++) {
      const nb = getNeighbor(q, r, s);
      const nbT = tiles[hexKey(nb.q, nb.r)];
      if (nbT && nbT.placed) {
        neighbourCount++;
        neighbourTerrainTypes.add(getMajorityType(nbT.sides));
      }
    }
    questManager.onTilePlaced({
      coord: { q, r },
      matchCount: totalStars,
      majorType,
      isOcean: tile.baseSpriteKey ? isWaterTile(tile.baseSpriteKey) : false,
      neighbourCount,
      neighbourTerrainTypes,
    });
  }

  return true;
}

/** Check which vacant tile categories exist for overlay placement. */
function getVacantTileTypes() {
  let hasVacantWater = false;
  let hasVacantGround = false;
  for (const key of Object.keys(tiles)) {
    const t = tiles[key];
    if (!t.placed || t.overlaySpriteKey) continue;
    if (t.baseSpriteKey && isWaterTile(t.baseSpriteKey)) {
      hasVacantWater = true;
    } else {
      hasVacantGround = true;
    }
    if (hasVacantWater && hasVacantGround) break;
  }
  return { hasVacantWater, hasVacantGround };
}

// ===== OVERLAY SYSTEM =====

/** Regular overlay families (unlocked via creative quests or 100-star milestone). */
const REGULAR_OVERLAY_FAMILIES = new Set([
  'forest', 'vegetation', 'mountainrocks', 'island_land', 'island_water',
]);

/** Special overlay families (unlocked only via special quests). */
const SPECIAL_OVERLAY_FAMILIES = new Set([
  'animal_land', 'animal_water', 'ship', 'structure', 'campsite',
  'castle_ruin', 'dragon', 'geysers', 'flying_castle', 'graveyard',
]);

function getRegularLockedOverlays() {
  return overlayUnlocks.locked.filter(k => REGULAR_OVERLAY_FAMILIES.has(getOverlayFamily(k)));
}

function getSpecialLockedOverlays() {
  return overlayUnlocks.locked.filter(k => SPECIAL_OVERLAY_FAMILIES.has(getOverlayFamily(k)));
}

/** Unlock one overlay from the given pool ('regular' or 'special'). Returns the key or null. */
function unlockOverlayFromPool(pool) {
  const candidates = pool === 'regular' ? getRegularLockedOverlays() : getSpecialLockedOverlays();
  if (candidates.length === 0) return null;
  const pick = candidates[Math.floor(Math.random() * candidates.length)];
  const idx = overlayUnlocks.locked.indexOf(pick);
  overlayUnlocks.locked.splice(idx, 1);
  overlayUnlocks.unlocked.push(pick);
  return pick;
}

function initOverlayState() {
  if (spriteIndex) {
    overlayUnlocks.locked = [...spriteIndex.overlayKeysAll];
    overlayUnlocks.unlocked = [];
    overlayUnlocks.starsSinceLastUnlock = 0;
  }
}

/** Called when stars are added — check 100-star regular overlay milestone. */
function checkOverlayUnlock() {
  let unlocked = false;
  while (
    overlayUnlocks.starsSinceLastUnlock >= OVERLAY_UNLOCK_CADENCE &&
    getRegularLockedOverlays().length > 0
  ) {
    overlayUnlocks.starsSinceLastUnlock -= OVERLAY_UNLOCK_CADENCE;
    const pick = unlockOverlayFromPool('regular');
    if (pick) {
      pendingUnlockNotifs.push(pick);
      Analytics.track("overlay_unlock", {
        build: String(BUILD_NUMBER),
        source: "star_milestone",
        unlocked_total: overlayUnlocks.unlocked.length,
        locked_remaining: overlayUnlocks.locked.length,
      });
      unlocked = true;
    }
  }
  if (unlocked) {
    audio.playSfx("overlay_unlock");
    updateHUD();
    showNextUnlockNotif();
  }
}

function spawnConfetti() {
  const colors = [
    "#ff6b6b",
    "#ffd93d",
    "#6bcb77",
    "#4d96ff",
    "#ff6bef",
    "#ffa36b",
  ];
  const modal = document.getElementById("unlock-modal");
  for (let i = 0; i < 30; i++) {
    const piece = document.createElement("div");
    piece.className = "confetti-piece";
    piece.style.background = colors[Math.floor(Math.random() * colors.length)];
    piece.style.left = Math.random() * 100 + "vw";
    piece.style.top = -10 - Math.random() * 20 + "px";
    piece.style.setProperty("--cx", Math.random() * 200 - 100 + "px");
    piece.style.animationDuration = 1.5 + Math.random() + "s";
    piece.style.animationDelay = Math.random() * 0.5 + "s";
    modal.appendChild(piece);
  }
}

function clearConfetti() {
  const modal = document.getElementById("unlock-modal");
  modal.querySelectorAll(".confetti-piece").forEach((el) => el.remove());
}

function showNextUnlockNotif() {
  if (pendingUnlockNotifs.length === 0) return;
  const modal = document.getElementById("unlock-modal");
  if (modal.classList.contains("visible")) return;

  const overlayKey = pendingUnlockNotifs.shift();
  const previewCanvas = document.getElementById("unlock-preview");
  const pctx = previewCanvas.getContext("2d");
  pctx.clearRect(0, 0, 80, 80);
  if (spriteAtlas && spriteAtlas.has(overlayKey)) {
    const scale = 80 / 30;
    pctx.save();
    pctx.scale(scale, scale);
    spriteAtlas.draw(pctx, overlayKey, 0, SPRITE_OFFSET_Y);
    pctx.restore();
  }

  clearConfetti();
  spawnConfetti();
  modal.classList.add("visible");
  audio.playSfx("ui_popup_open");
}

// Prevent unlock modal clicks from bubbling to the canvas/window placement handler
const unlockModal = document.getElementById("unlock-modal");
unlockModal.addEventListener("mousedown", (e) => e.stopPropagation());
unlockModal.addEventListener("mouseup", (e) => e.stopPropagation());
unlockModal.addEventListener("click", (e) => e.stopPropagation());

document.getElementById("unlock-ok").addEventListener("click", () => {
  audio.playSfx("ui_popup_close");
  unlockModal.classList.remove("visible");
  clearConfetti();
  setTimeout(() => showNextUnlockNotif(), 200);
});

function placeOverlayFromHand(q, r) {
  const key = hexKey(q, r);
  const tile = tiles[key];
  if (!tile || !tile.placed) return false;
  if (tile.overlaySpriteKey) return false;

  const handItem = hand[selectedHandIndex];
  if (!handItem || !handItem.isOverlay) return false;
  const overlayKey = handItem.overlayKey;

  const tileIsWater = tile.baseSpriteKey
    ? isWaterTile(tile.baseSpriteKey)
    : false;
  const overlayIsWater = isWaterOverlay(overlayKey);
  if (tileIsWater !== overlayIsWater) return false;

  Analytics.startSessionIfNeeded({ build: String(BUILD_NUMBER), first_action: "overlay_place" });

  tile.overlaySpriteKey = overlayKey;

  const px = hexToPixel(q, r, HEX_SIZE);
  placementAnims.push({
    x: px.x,
    y: px.y,
    t: performance.now(),
    duration: 300,
  });

  // Sparkle burst on overlay placement
  renderer.triggerOverlaySparkles(q, r);

  // Compute overlay economy reward
  const majorType = getMajorityType(tile.sides);
  let adjacentOverlays = 0;
  for (let s = 0; s < 6; s++) {
    const nb = getNeighbor(q, r, s);
    const neighbor = tiles[hexKey(nb.q, nb.r)];
    if (neighbor && neighbor.overlaySpriteKey) adjacentOverlays++;
  }

  const reward = computeOverlayPlacementReward(
    overlayKey,
    majorType,
    adjacentOverlays
  );

  // Award stars
  score.stars += reward.stars;
  overlayUnlocks.starsSinceLastUnlock += reward.stars;
  checkOverlayUnlock();

  animations.push({
    x: px.x,
    y: px.y - 10,
    text: "\u2b50 +" + reward.stars,
    startTime: performance.now(),
    duration: 1000,
    isScore: true,
  });

  // Award resources
  for (const [resType, amount] of Object.entries(reward.resources)) {
    resources[resType] = (resources[resType] || 0) + amount;
    const emoji = RESOURCE_EMOJI[resType];
    if (emoji) {
      for (let i = 0; i < Math.min(amount, 5); i++) {
        animations.push({
          x: px.x + (Math.random() - 0.5) * 20,
          y: px.y,
          text: `+${amount > 1 && i === 0 ? amount + " " : ""}${emoji}`,
          startTime: performance.now() + i * 80,
          duration: 800,
        });
        if (amount > 1 && i === 0) break; // show single "+N emoji" for multi
      }
    }
  }

  checkAbilityUnlocks();
  Analytics.track("overlay_place", {
    build: String(BUILD_NUMBER),
    family: getOverlayFamily(overlayKey),
    adj_bucket: _adjBucket(adjacentOverlays),
    terrain: majorType,
  });

  // Ensure storage unlocks when threshold met
  if (isStorageUnlocked() && storageCapacity < 1) {
    storageCapacity = 1;
  }

  rerollChainCount = 0;

  renderer.upsertTile(tile);

  audio.playSfx("overlay_place");
  if (reward.stars > 0) audio.playSfx("reward_star");
  const hasResources = Object.values(reward.resources).some((v) => v > 0);
  if (hasResources) audio.playSfx("resource_gain");

  hand.splice(selectedHandIndex, 1);
  fillHand();
  updateHUD();
  renderStoragePanel();
  saveGame();

  // Quest v2: build overlay placement event
  {
    const overlayFamily = getOverlayFamily(overlayKey);
    const econ = OVERLAY_ECONOMY[overlayFamily];
    const onBonusTerrain = econ ? econ.bonusTerrains.includes(majorType) : false;
    questManager.onOverlayPlaced({
      coord: { q, r },
      overlayFamily,
      adjacentOverlays,
      onBonusTerrain,
      isOcean: tile.baseSpriteKey ? isWaterTile(tile.baseSpriteKey) : false,
    });
  }

  return true;
}

// ===== REROLL ABILITY =====
function rerollSlot(i) {
  if (!isRerollUnlocked()) return;
  const cost = getRerollCost(rerollChainCount);
  if (!canAfford(cost)) return;
  Analytics.startSessionIfNeeded({ build: String(BUILD_NUMBER), first_action: "reroll" });
  const chainBucketVal = _chainBucket(rerollChainCount);
  payCost(cost);
  hand[i] = generateTileForSlot(i);
  rerollChainCount++;
  Analytics.track("reroll", {
    build: String(BUILD_NUMBER),
    slot: _SLOT_NAMES[i] || "slot" + i,
    chain_bucket: chainBucketVal,
  });
  renderHand();
  updateHUD();
  saveGame();
}

// ===== STORAGE ABILITY =====
function storeFromHand(i) {
  if (!isStorageUnlocked()) return;
  // Find first empty slot within capacity
  let emptyIdx = -1;
  for (let s = 0; s < storageCapacity; s++) {
    if (storage[s] === null) {
      emptyIdx = s;
      break;
    }
  }
  if (emptyIdx === -1) return; // full

  storage[emptyIdx] = hand[i];
  hand[i] = generateTileForSlot(i);
  renderHand();
  renderStoragePanel();
  updateHUD();
  saveGame();
}

function placeFromStorage(idx, q, r) {
  if (idx < 0 || idx >= storageCapacity || !storage[idx]) return false;
  const item = storage[idx];

  if (item.isOverlay) {
    // Place overlay from storage
    const key = hexKey(q, r);
    const tile = tiles[key];
    if (!tile || !tile.placed || tile.overlaySpriteKey) return false;
    const tileIsWater = tile.baseSpriteKey
      ? isWaterTile(tile.baseSpriteKey)
      : false;
    const overlayIsWater = isWaterOverlay(item.overlayKey);
    if (tileIsWater !== overlayIsWater) return false;

    Analytics.startSessionIfNeeded({ build: String(BUILD_NUMBER), first_action: "overlay_place" });

    tile.overlaySpriteKey = item.overlayKey;

    const px = hexToPixel(q, r, HEX_SIZE);
    placementAnims.push({
      x: px.x,
      y: px.y,
      t: performance.now(),
      duration: 300,
    });

    // Sparkle burst on overlay placement from storage
    renderer.triggerOverlaySparkles(q, r);

    const majorType = getMajorityType(tile.sides);
    let adjacentOverlays = 0;
    for (let s = 0; s < 6; s++) {
      const nb = getNeighbor(q, r, s);
      const neighbor = tiles[hexKey(nb.q, nb.r)];
      if (neighbor && neighbor.overlaySpriteKey) adjacentOverlays++;
    }
    const reward = computeOverlayPlacementReward(
      item.overlayKey,
      majorType,
      adjacentOverlays
    );
    score.stars += reward.stars;
    overlayUnlocks.starsSinceLastUnlock += reward.stars;
    checkOverlayUnlock();
    animations.push({
      x: px.x,
      y: px.y - 10,
      text: "\u2b50 +" + reward.stars,
      startTime: performance.now(),
      duration: 1000,
      isScore: true,
    });
    for (const [resType, amount] of Object.entries(reward.resources)) {
      resources[resType] = (resources[resType] || 0) + amount;
      const emoji = RESOURCE_EMOJI[resType];
      if (emoji) {
        animations.push({
          x: px.x + (Math.random() - 0.5) * 20,
          y: px.y,
          text: `+${amount} ${emoji}`,
          startTime: performance.now(),
          duration: 800,
        });
      }
    }
    checkAbilityUnlocks();
    Analytics.track("overlay_place", {
      build: String(BUILD_NUMBER),
      family: getOverlayFamily(item.overlayKey),
      adj_bucket: _adjBucket(adjacentOverlays),
      terrain: majorType,
    });
    renderer.upsertTile(tile);
  } else {
    // Place tile from storage
    if (tiles[hexKey(q, r)]) return false;
    const valid = getValidPlacements();
    if (!valid.has(hexKey(q, r))) return false;

    Analytics.startSessionIfNeeded({ build: String(BUILD_NUMBER), first_action: "tile_place" });

    item.coord = { q, r };
    item.placed = true;
    item.placedAtMs = performance.now();
    tiles[hexKey(q, r)] = item;
    renderer.assignSpriteKeys(item);

    const px = hexToPixel(q, r, HEX_SIZE);
    placementAnims.push({
      x: px.x,
      y: px.y,
      t: performance.now(),
      duration: 300,
    });
    renderer.upsertTile(item);

    // Star counting for matching sides
    let totalStars = 0;
    for (let s = 0; s < 6; s++) {
      const nb = getNeighbor(q, r, s);
      const neighbor = tiles[hexKey(nb.q, nb.r)];
      if (!neighbor) continue;
      const mySideType = getEffectiveSide(item.sides, item.rotation, s);
      const theirSideType = getEffectiveSide(
        neighbor.sides,
        neighbor.rotation,
        oppositeSide(s)
      );
      if (mySideType === theirSideType) totalStars++;
    }

    if (totalStars > 0) {
      score.stars += totalStars;
      overlayUnlocks.starsSinceLastUnlock += totalStars;
      checkOverlayUnlock();
      animations.push({
        x: px.x,
        y: px.y - 10,
        text: "\u2b50 +" + totalStars,
        startTime: performance.now(),
        duration: 1000,
        isScore: true,
      });
    }
    checkAbilityUnlocks();
    Analytics.track("tile_place", {
      build: String(BUILD_NUMBER),
      source: "storage",
      tile_sig: tileSignatureFromSides(item.sides),
      stars_bucket: _starsBucket(totalStars),
    });
  }

  if (isStorageUnlocked() && storageCapacity < 1) {
    storageCapacity = 1;
  }

  rerollChainCount = 0;
  storage[idx] = null;
  selectedStorageIndex = -1;

  updateHUD();
  renderHand();
  renderStoragePanel();
  saveGame();

  if (!item.isOverlay) {
    rebuildAdjacencyAt({ q, r });
  }

  if (item.isOverlay) {
    // Quest v2: overlay placed from storage
    const _sFam = getOverlayFamily(item.overlayKey);
    const _sEcon = OVERLAY_ECONOMY[_sFam];
    const _sMajor = getMajorityType(tiles[hexKey(q, r)].sides);
    let _sAdj = 0;
    for (let s = 0; s < 6; s++) {
      const nb = getNeighbor(q, r, s);
      if (tiles[hexKey(nb.q, nb.r)]?.overlaySpriteKey) _sAdj++;
    }
    questManager.onOverlayPlaced({
      coord: { q, r },
      overlayFamily: _sFam,
      adjacentOverlays: _sAdj,
      onBonusTerrain: _sEcon ? _sEcon.bonusTerrains.includes(_sMajor) : false,
      isOcean: item.isOverlay && isWaterTile(tiles[hexKey(q, r)]?.baseSpriteKey || ''),
    });
  } else {
    // Quest v2: tile placed from storage
    const _sT = tiles[hexKey(q, r)];
    const _sType = getMajorityType(_sT.sides);
    let _sNC = 0;
    const _sTT = new Set();
    for (let s = 0; s < 6; s++) {
      const nb = getNeighbor(q, r, s);
      const nbT = tiles[hexKey(nb.q, nb.r)];
      if (nbT && nbT.placed) {
        _sNC++;
        _sTT.add(getMajorityType(nbT.sides));
      }
    }
    questManager.onTilePlaced({
      coord: { q, r },
      matchCount: 0, // stars already counted separately above
      majorType: _sType,
      isOcean: _sT.baseSpriteKey ? isWaterTile(_sT.baseSpriteKey) : false,
      neighbourCount: _sNC,
      neighbourTerrainTypes: _sTT,
    });
  }

  return true;
}

function upgradeStorage() {
  if (storageCapacity >= 3) return;
  if (storageCapacity < 1) return;
  const cost = getStorageUpgradeCost(storageCapacity);
  if (!canAfford(cost)) return;
  Analytics.startSessionIfNeeded({ build: String(BUILD_NUMBER), first_action: "storage_upgrade" });
  payCost(cost);
  storageCapacity++;
  Analytics.track("storage_upgrade", {
    build: String(BUILD_NUMBER),
    new_capacity: storageCapacity,
  });
  renderStoragePanel();
  updateHUD();
  saveGame();
}

// ===== DEMOLISH =====
function toggleDemolish() {
  demolishMode = !demolishMode;
  selectedStorageIndex = -1;
  const btn = document.getElementById("demolish-btn");
  btn.classList.toggle("active", demolishMode);
  canvas.classList.toggle("demolish-cursor", demolishMode);
}

function demolishAt(q, r) {
  const key = hexKey(q, r);
  const tile = tiles[key];
  if (!tile) return false;

  const cost = getDemolishCost(tile);
  if (score.stars < cost.stars) {
    // Show "can't afford" feedback
    audio.playSfx("invalid_action");
    const px = hexToPixel(q, r, HEX_SIZE);
    animations.push({
      x: px.x,
      y: px.y - 10,
      text: `Need ${formatCost(cost)}`,
      startTime: performance.now(),
      duration: 2400,
      isError: true,
    });
    return false;
  }
  Analytics.startSessionIfNeeded({ build: String(BUILD_NUMBER), first_action: "demolish_execute" });
  const demolishType = tile.overlaySpriteKey ? "overlay" : "tile";
  score.stars -= cost.stars;

  const px = hexToPixel(q, r, HEX_SIZE);
  if (tile.overlaySpriteKey) {
    // Remove overlay only
    tile.overlaySpriteKey = null;
    renderer.upsertTile(tile);
  } else {
    // Remove entire tile
    const coord = { q, r };
    delete tiles[key];
    renderer.removeTile(coord);
  }

  Analytics.track("demolish_execute", {
    build: String(BUILD_NUMBER),
    type: demolishType,
  });

  animations.push({
    x: px.x,
    y: px.y - 10,
    text: `-${formatCost(cost)}`,
    startTime: performance.now(),
    duration: 1000,
    isScore: false,
  });

  updateHUD();
  saveGame();
  return true;
}

document.getElementById("demolish-btn").addEventListener("click", (e) => {
  e.stopPropagation();
  audio.playSfx("ui_click");
  toggleDemolish();
});

// ===== PREVIEW PLACEMENT =====
function previewPlacement(hex) {
  if (!hex || !isPreviewUnlocked()) return null;
  const selected =
    selectedStorageIndex >= 0
      ? storage[selectedStorageIndex]
      : hand[selectedHandIndex];
  if (!selected) return null;

  if (selected.isOverlay) {
    const key = hexKey(hex.q, hex.r);
    const tile = tiles[key];
    if (!tile || !tile.placed || tile.overlaySpriteKey) return null;
    const tileIsWater = tile.baseSpriteKey
      ? isWaterTile(tile.baseSpriteKey)
      : false;
    const overlayIsWater = isWaterOverlay(selected.overlayKey);
    if (tileIsWater !== overlayIsWater) return null;

    const majorType = getMajorityType(tile.sides);
    let adjacentOverlays = 0;
    for (let s = 0; s < 6; s++) {
      const nb = getNeighbor(hex.q, hex.r, s);
      const neighbor = tiles[hexKey(nb.q, nb.r)];
      if (neighbor && neighbor.overlaySpriteKey) adjacentOverlays++;
    }
    return computeOverlayPlacementReward(
      selected.overlayKey,
      majorType,
      adjacentOverlays
    );
  } else {
    // Tile placement preview: count matching sides for stars only
    const valid = getValidPlacements();
    if (!valid.has(hexKey(hex.q, hex.r))) return null;

    let totalStars = 0;
    for (let s = 0; s < 6; s++) {
      const nb = getNeighbor(hex.q, hex.r, s);
      const neighbor = tiles[hexKey(nb.q, nb.r)];
      if (!neighbor) continue;
      const mySideType = getEffectiveSide(selected.sides, selected.rotation, s);
      const theirSideType = getEffectiveSide(
        neighbor.sides,
        neighbor.rotation,
        oppositeSide(s)
      );
      if (mySideType === theirSideType) totalStars++;
    }
    if (totalStars === 0) return null;
    return { stars: totalStars, resources: {} };
  }
}

// ===== ADJACENCY TRANSITION HELPERS =====
function rebuildAdjacencyAt(coord) {
  rebuildRenderMetaAround(coord, tiles, spriteIndex?.tileTransitionKeys ?? null);
}

// ===== SYNC HELPERS =====
function syncRendererTiles() {
  const tileMap = new Map();
  for (const [key, tile] of Object.entries(tiles)) {
    tileMap.set(key, tile);
  }
  renderer.setTiles(tileMap);
}

function syncRendererCamera() {
  renderer.setCamera(camera);
}

function syncRendererState() {
  syncRendererTiles();
  syncRendererCamera();
  renderer.validPlacements = getValidPlacements();
}

// ===== CAMERA HELPERS =====
function worldToScreen(wx, wy) {
  return {
    x: (wx - camera.x) * camera.zoom + canvas.width / 2,
    y: (wy - camera.y) * camera.zoom + canvas.height / 2,
  };
}

// ===== ANIMATION =====
function easeOutBack(t) {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}

// ===== RENDER =====
function render(now) {
  syncRendererCamera();

  // Update ghost tile and overlay ghost
  if (demolishMode) {
    renderer.ghostTile = null;
    renderer.ghostOverlayKey = null;
  } else if (selectedStorageIndex >= 0 && storage[selectedStorageIndex]) {
    const stored = storage[selectedStorageIndex];
    if (stored.isOverlay) {
      renderer.ghostTile = null;
      renderer.ghostOverlayKey = stored.overlayKey;
    } else {
      renderer.ghostTile = stored;
      renderer.ghostOverlayKey = null;
    }
  } else {
    const selected = hand[selectedHandIndex];
    if (selected && !selected.isOverlay) {
      renderer.ghostTile = selected;
      renderer.ghostOverlayKey = null;
    } else {
      renderer.ghostTile = null;
      renderer.ghostOverlayKey =
        selected && selected.isOverlay ? selected.overlayKey : null;
    }
  }
  renderer.hoveredHex = hoveredHex;
  renderer.validPlacements = getValidPlacements();

  // Cloud + shadow pipeline
  zoomTracker.update(camera.zoom);
  const dt = now - _lastFrameTime;
  _lastFrameTime = now;

  // Update weather state before clouds (reads biome to drive cloud density/precip)
  const biomeResult = resolveBiome(
    { cameraX: camera.x, cameraY: camera.y, zoom: camera.zoom },
    getBiomeResolveDeps()
  );
  weatherState.update(dt, now, biomeResult);

  cloudSystem.update(dt, camera, canvas.width, canvas.height);
  cloudShadowRenderer.render(
    cloudSystem,
    camera,
    canvas.width,
    canvas.height,
    now
  );

  renderer.update(now);
  renderer.render(now);

  // Render clouds on dedicated canvas (above game, below UI) — no masking, clouds float freely
  cloudRenderer.render(
    cloudsCtx,
    cloudSystem,
    camera,
    canvas.width,
    canvas.height,
    now,
    weatherState
  );

  // Time-of-day post-processing overlay
  if (worldClock.enabled) {
    const phase = worldClock.getPhase01(now);
    const preset = worldClock.getPreset(phase);
    postFx.render(
      postfxCtx,
      canvas.width,
      canvas.height,
      preset,
      worldClock.strength
    );
  } else {
    postfxCtx.clearRect(0, 0, canvas.width, canvas.height);
  }
  updateClockDisplay(now);

  // Precipitation overlay (after PostFx since PostFx clears canvas)
  const dtSec = Math.min(dt / 1000, 0.1);
  precipRenderer.render(postfxCtx, canvas.width, canvas.height, dtSec, weatherState, cloudSystem, camera);

  // Draw placement scale animations on top
  const sz = HEX_SIZE * camera.zoom;
  for (let i = placementAnims.length - 1; i >= 0; i--) {
    const anim = placementAnims[i];
    const elapsed = now - anim.t;
    const prog = Math.min(1, elapsed / anim.duration);
    if (prog >= 1) {
      placementAnims.splice(i, 1);
      continue;
    }
    const scale = 0.5 + 0.5 * easeOutBack(prog);
    const alpha = prog;
    const sp = worldToScreen(anim.x, anim.y);

    for (const [, t] of Object.entries(tiles)) {
      const wp = hexToPixel(t.coord.q, t.coord.r, HEX_SIZE);
      if (Math.abs(wp.x - anim.x) < 1 && Math.abs(wp.y - anim.y) < 1) {
        ctx.save();
        ctx.globalAlpha = alpha;
        if (spriteAtlas && t.baseSpriteKey) {
          const s = sz * scale;
          const drawScale = (2 * s) / 30;
          const dx = sp.x - 15 * drawScale;
          const dy = sp.y - 15 * drawScale;
          ctx.translate(dx, dy);
          ctx.scale(drawScale, drawScale);
          spriteAtlas.draw(ctx, t.baseSpriteKey, 0, SPRITE_OFFSET_Y);
          if (t.transitionSpriteKey && spriteAtlas.has(t.transitionSpriteKey)) {
            spriteAtlas.draw(ctx, t.transitionSpriteKey, 0, SPRITE_OFFSET_Y);
          }
          if (t.overlaySpriteKey && spriteAtlas.has(t.overlaySpriteKey)) {
            spriteAtlas.draw(ctx, t.overlaySpriteKey, 0, SPRITE_OFFSET_Y);
          }
        } else {
          drawBlueprintTile(ctx, t, sp.x, sp.y, sz * scale, alpha);
        }
        ctx.restore();
        break;
      }
    }
  }

  // Draw floating animations (score/resource popups)
  for (let i = animations.length - 1; i >= 0; i--) {
    const a = animations[i];
    const elapsed = now - a.startTime;
    if (elapsed < 0) continue;
    const prog = elapsed / a.duration;
    if (prog > 1) {
      animations.splice(i, 1);
      continue;
    }
    const sp = worldToScreen(a.x, a.y);
    const offsetY = -40 * prog;
    if (a.isError) {
      // Hold full opacity for first 40% of duration, then fade out
      const fadeStart = 0.4;
      ctx.globalAlpha =
        prog < fadeStart ? 1 : 1 - (prog - fadeStart) / (1 - fadeStart);
      ctx.font = "14px sans-serif";
      ctx.textAlign = "center";
      const tm = ctx.measureText(a.text);
      const px = 8,
        py = 4;
      const bx = sp.x - tm.width / 2 - px;
      const by = sp.y + offsetY - 12 - py;
      const bw = tm.width + px * 2;
      const bh = 16 + py * 2;
      ctx.fillStyle = "rgba(0, 0, 0, 0.55)";
      ctx.beginPath();
      ctx.roundRect(bx, by, bw, bh, bh / 2);
      ctx.fill();
      ctx.fillStyle = "#ff5555";
    } else {
      ctx.globalAlpha = 1 - prog;
      ctx.font = a.isScore ? "bold 18px sans-serif" : "20px sans-serif";
      ctx.textAlign = "center";
      ctx.fillStyle = a.isScore ? "#ffd700" : "#fff";
    }
    ctx.fillText(a.text, sp.x, sp.y + offsetY);
    ctx.globalAlpha = 1;
  }

  // Preview tooltip
  updatePreviewTooltip();

  requestAnimationFrame(render);
}

// ===== PREVIEW TOOLTIP =====
function updatePreviewTooltip() {
  const tooltip = document.getElementById("preview-tooltip");
  if (!hoveredHex || !isPreviewUnlocked() || demolishMode) {
    tooltip.style.display = "none";
    return;
  }
  const preview = previewPlacement(hoveredHex);
  if (!preview) {
    tooltip.style.display = "none";
    return;
  }

  let text = `\u2b50+${preview.stars}`;
  for (const [resType, amount] of Object.entries(preview.resources)) {
    if (amount > 0) {
      text += ` ${RESOURCE_EMOJI[resType]}+${amount}`;
    }
  }

  const px = hexToPixel(hoveredHex.q, hoveredHex.r, HEX_SIZE);
  const sp = worldToScreen(px.x, px.y);

  tooltip.textContent = text;
  tooltip.style.display = "block";
  tooltip.style.left = sp.x + 20 + "px";
  tooltip.style.top = sp.y - 30 + "px";
}

// ===== WORLD CLOCK DISPLAY =====
const _clockEl = document.getElementById("world-clock-display");
let _lastClockText = "";

function updateClockDisplay(now) {
  if (!worldClock.enabled) {
    if (_clockEl.style.display !== "none") _clockEl.style.display = "none";
    return;
  }
  if (_clockEl.style.display === "none") _clockEl.style.display = "";
  const phase = worldClock.getPhase01(now);
  // Map phase 0..1 to 24-hour clock (phase 0 = midnight)
  const totalMinutes = Math.floor(phase * 24 * 60);
  const hours = Math.floor(totalMinutes / 60) % 24;
  const minutes = totalMinutes % 60;
  const h12 = hours % 12 || 12;
  const ampm = hours < 12 ? "AM" : "PM";
  const icon = hours >= 6 && hours < 18 ? "\u2600\uFE0F" : "\uD83C\uDF19";
  const text = `${icon} ${h12}:${String(minutes).padStart(2, "0")} ${ampm}`;
  if (text !== _lastClockText) {
    _clockEl.textContent = text;
    _lastClockText = text;
  }
}

// ===== HUD =====
function updateHUD() {
  const resDiv = document.getElementById("resources");
  resDiv.innerHTML = "";
  for (const res of RESOURCE_TYPES) {
    const item = document.createElement("div");
    item.className = "resource-item";
    item.innerHTML = `<span class="icon">${
      RESOURCE_EMOJI[res]
    }</span><span class="count">${resources[res] || 0}</span>`;
    resDiv.appendChild(item);
  }
  document.getElementById(
    "score-display"
  ).textContent = `\u2b50 ${score.stars}`;
}

// ===== HAND RENDERING =====
function drawTileOnMiniCanvas(tctx, tile, cx, cy, size) {
  tctx.clearRect(0, 0, 80, 80);
  if (spriteAtlas && tile.baseSpriteKey) {
    const scale = (2 * size) / 30;
    const dx = cx - 15 * scale;
    const dy = cy - 15 * scale;
    tctx.save();
    tctx.translate(dx, dy);
    tctx.scale(scale, scale);
    spriteAtlas.draw(tctx, tile.baseSpriteKey, 0, SPRITE_OFFSET_Y);
    if (tile.transitionSpriteKey && spriteAtlas.has(tile.transitionSpriteKey)) {
      spriteAtlas.draw(tctx, tile.transitionSpriteKey, 0, SPRITE_OFFSET_Y);
    }
    tctx.restore();
  } else {
    drawBlueprintTile(tctx, tile, cx, cy, size, 1, false, false);
  }
}

function drawOverlayOnMiniCanvas(tctx, overlayKey) {
  tctx.clearRect(0, 0, 80, 80);
  if (spriteAtlas && spriteAtlas.has(overlayKey)) {
    const scale = 80 / 30;
    tctx.save();
    tctx.scale(scale, scale);
    spriteAtlas.draw(tctx, overlayKey, 0, SPRITE_OFFSET_Y);
    tctx.restore();
  }
}

function renderHand() {
  const handDiv = document.getElementById("hand");
  handDiv.innerHTML = "";
  hand.forEach((item, i) => {
    const slot = document.createElement("div");
    slot.className = "hand-slot";

    const wrapper = document.createElement("div");
    let cls = "hand-tile";
    if (item.isOverlay) cls += " overlay-item";
    if (i === selectedHandIndex && selectedStorageIndex < 0) cls += " selected";
    wrapper.className = cls;
    const c = document.createElement("canvas");
    c.width = 80;
    c.height = 80;
    wrapper.appendChild(c);
    const tctx = c.getContext("2d");
    if (item.isOverlay) {
      drawOverlayOnMiniCanvas(tctx, item.overlayKey);
    } else {
      drawTileOnMiniCanvas(tctx, item, 40, 40, 32);
    }
    wrapper.addEventListener("click", (e) => {
      e.stopPropagation();
      audio.playSfx("ui_click");
      selectedHandIndex = i;
      selectedStorageIndex = -1;
      demolishMode = false;
      document.getElementById("demolish-btn").classList.remove("active");
      canvas.classList.remove("demolish-cursor");
      renderHand();
      renderStoragePanel();
    });
    slot.appendChild(wrapper);

    // Ability buttons below tile
    const btns = document.createElement("div");
    btns.className = "hand-slot-buttons";

    // Reroll button
    const rerollBtn = document.createElement("button");
    rerollBtn.textContent = "\uD83D\uDD04"; // 🔄
    rerollBtn.title = isRerollUnlocked()
      ? `Reroll (${formatCost(getRerollCost(rerollChainCount))})`
      : "Unlock at 50\u2b50";
    if (!isRerollUnlocked()) {
      rerollBtn.classList.add("locked");
      rerollBtn.disabled = true;
    } else if (!canAfford(getRerollCost(rerollChainCount))) {
      rerollBtn.disabled = true;
    }
    rerollBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      audio.playSfx("ui_click");
      rerollSlot(i);
    });
    btns.appendChild(rerollBtn);

    // Store button
    const storeBtn = document.createElement("button");
    storeBtn.textContent = "\uD83D\uDCE6"; // 📦
    const storageFull = (() => {
      for (let s = 0; s < storageCapacity; s++) {
        if (storage[s] === null) return false;
      }
      return true;
    })();
    storeBtn.title = isStorageUnlocked()
      ? storageFull
        ? "Storage full"
        : "Store tile"
      : "Unlock at 100\u2b50";
    if (!isStorageUnlocked()) {
      storeBtn.classList.add("locked");
      storeBtn.disabled = true;
    } else if (storageFull) {
      storeBtn.disabled = true;
    }
    storeBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      audio.playSfx("ui_click");
      storeFromHand(i);
    });
    btns.appendChild(storeBtn);

    slot.appendChild(btns);
    handDiv.appendChild(slot);
  });
}

// ===== STORAGE PANEL =====
function renderStoragePanel() {
  const panel = document.getElementById("storage-panel");
  panel.innerHTML = "";

  if (!isStorageUnlocked()) {
    panel.classList.remove("visible");
    return;
  }
  panel.classList.add("visible");

  const label = document.createElement("span");
  label.className = "storage-label";
  const occupied = storage.filter(
    (s, i) => i < storageCapacity && s !== null
  ).length;
  label.textContent = `Storage (${occupied}/${storageCapacity})`;
  panel.appendChild(label);

  for (let idx = 0; idx < 3; idx++) {
    const slotDiv = document.createElement("div");
    slotDiv.className = "storage-slot";
    const c = document.createElement("canvas");
    c.width = 60;
    c.height = 60;
    slotDiv.appendChild(c);

    if (idx >= storageCapacity) {
      slotDiv.classList.add("locked-slot");
    } else if (storage[idx] === null) {
      slotDiv.classList.add("empty");
    } else {
      // Draw stored item
      const tctx = c.getContext("2d");
      const item = storage[idx];
      if (item.isOverlay) {
        tctx.clearRect(0, 0, 60, 60);
        if (spriteAtlas && spriteAtlas.has(item.overlayKey)) {
          const scale = 60 / 30;
          tctx.save();
          tctx.scale(scale, scale);
          spriteAtlas.draw(tctx, item.overlayKey, 0, SPRITE_OFFSET_Y);
          tctx.restore();
        }
      } else {
        tctx.clearRect(0, 0, 60, 60);
        if (spriteAtlas && item.baseSpriteKey) {
          const size = 24;
          const scale = (2 * size) / 30;
          const cx = 30,
            cy = 30;
          const dx = cx - 15 * scale;
          const dy = cy - 15 * scale;
          tctx.save();
          tctx.translate(dx, dy);
          tctx.scale(scale, scale);
          spriteAtlas.draw(tctx, item.baseSpriteKey, 0, SPRITE_OFFSET_Y);
          if (
            item.transitionSpriteKey &&
            spriteAtlas.has(item.transitionSpriteKey)
          ) {
            spriteAtlas.draw(
              tctx,
              item.transitionSpriteKey,
              0,
              SPRITE_OFFSET_Y
            );
          }
          tctx.restore();
        } else {
          drawBlueprintTile(tctx, item, 30, 30, 24, 1, false, false);
        }
      }

      if (idx === selectedStorageIndex) {
        slotDiv.classList.add("selected-storage");
      }

      slotDiv.addEventListener("click", (e) => {
        e.stopPropagation();
        if (selectedStorageIndex === idx) {
          selectedStorageIndex = -1;
        } else {
          selectedStorageIndex = idx;
          demolishMode = false;
          document.getElementById("demolish-btn").classList.remove("active");
          canvas.classList.remove("demolish-cursor");
        }
        renderHand();
        renderStoragePanel();
      });
    }

    panel.appendChild(slotDiv);
  }

  // Upgrade button
  if (storageCapacity < 3) {
    const upgradeBtn = document.createElement("button");
    upgradeBtn.id = "storage-upgrade-btn";
    const cost = getStorageUpgradeCost(storageCapacity);
    upgradeBtn.textContent = `Upgrade (${formatCost(cost)})`;
    if (!canAfford(cost)) {
      upgradeBtn.disabled = true;
    }
    upgradeBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      upgradeStorage();
    });
    panel.appendChild(upgradeBtn);
  }
}

// ===== INPUT =====
let isDragging = false;
let dragStart = { x: 0, y: 0 };
let dragCameraStart = { x: 0, y: 0 };
let hoveredHex = null;
let hasDragged = false;
let pointerOverUI = false;

// Track when pointer is over UI panels to prevent grid interaction
for (const uiId of ["hand", "storage-panel"]) {
  const el = document.getElementById(uiId);
  el.addEventListener("mouseenter", () => {
    pointerOverUI = true;
    hoveredHex = null;
  });
  el.addEventListener("mouseleave", () => {
    pointerOverUI = false;
  });
}

canvas.addEventListener("mousedown", (e) => {
  if (e.button === 0) {
    isDragging = true;
    hasDragged = false;
    dragStart = { x: e.clientX, y: e.clientY };
    dragCameraStart = { x: camera.x, y: camera.y };
    canvas.classList.add("grabbing");
  }
});

window.addEventListener("mousemove", (e) => {
  if (isDragging) {
    const dx = e.clientX - dragStart.x;
    const dy = e.clientY - dragStart.y;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) hasDragged = true;
    camera.x = dragCameraStart.x - dx / camera.zoom;
    camera.y = dragCameraStart.y - dy / camera.zoom;
    resolveAndUpdateAmbience(false);
  }
  if (!pointerOverUI) {
    hoveredHex = renderer.screenToAxial(e.clientX, e.clientY);
  }
});

window.addEventListener("mouseup", (e) => {
  if (e.button === 0) {
    if (!hasDragged && hoveredHex && !pointerOverUI) {
      if (demolishMode) {
        demolishAt(hoveredHex.q, hoveredHex.r);
      } else if (selectedStorageIndex >= 0) {
        placeFromStorage(selectedStorageIndex, hoveredHex.q, hoveredHex.r);
      } else {
        const selectedItem = hand[selectedHandIndex];
        if (selectedItem && selectedItem.isOverlay) {
          placeOverlayFromHand(hoveredHex.q, hoveredHex.r);
        } else {
          const valid = getValidPlacements();
          const hk = hexKey(hoveredHex.q, hoveredHex.r);
          if (valid.has(hk)) {
            placeTile(hoveredHex.q, hoveredHex.r);
          } else if (tiles[hk]) {
            audio.playSfx("invalid_action");
          }
        }
      }
    }
    if (hasDragged) {
      resolveAndUpdateAmbience(true);
    }
    isDragging = false;
    canvas.classList.remove("grabbing");
  }
});

canvas.addEventListener("contextmenu", (e) => {
  e.preventDefault();
  rotateTile(1);
});

canvas.addEventListener(
  "wheel",
  (e) => {
    e.preventDefault();
    const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
    camera.zoom = Math.max(0.3, Math.min(3, camera.zoom * zoomFactor));
    resolveAndUpdateAmbience(false);
  },
  { passive: false }
);

// Keyboard
const keysDown = new Set();
window.addEventListener("keydown", (e) => {
  keysDown.add(e.key.toLowerCase());
  if (e.key === "q" || e.key === "Q") rotateTile(-1);
  if (e.key === "e" || e.key === "E") rotateTile(1);
  if (e.key === "r" || e.key === "R") showResetModal();
  if (e.key === "1") {
    selectedHandIndex = 0;
    selectedStorageIndex = -1;
    renderHand();
    renderStoragePanel();
  }
  if (e.key === "2" && hand.length > 1) {
    selectedHandIndex = 1;
    selectedStorageIndex = -1;
    renderHand();
    renderStoragePanel();
  }
  if (e.key === "3" && hand.length > 2) {
    selectedHandIndex = 2;
    selectedStorageIndex = -1;
    renderHand();
    renderStoragePanel();
  }
  // Time-of-day debug shortcuts: [ rewind, ] fast-forward (10% of cycle each)
  if (e.key === "[") {
    worldClock.startMs += worldClock.cycleMs * 0.0001;
  }
  if (e.key === "]") {
    worldClock.startMs -= worldClock.cycleMs * 0.0001;
  }
  // Weather debug shortcuts: 5 = rain, 6 = snow, 7 = clear (back to normal)
  if (e.key === "5") weatherState.forceMode('rain');
  if (e.key === "6") weatherState.forceMode('snow');
  if (e.key === "7") weatherState.forceMode(null);
  // Adjacency debug toggles (non-conflicting keys)
  if (e.key === "`") renderer.debugFlags.hideAdjacency = !renderer.debugFlags.hideAdjacency;
  if (e.key === "i" || e.key === "I") renderer.debugFlags.hideInternal = !renderer.debugFlags.hideInternal;
  if (e.key === "t" || e.key === "T") renderer.debugFlags.showRuns = !renderer.debugFlags.showRuns;
});
window.addEventListener("keyup", (e) => keysDown.delete(e.key.toLowerCase()));

function updateKeyPan() {
  const speed = 5 / camera.zoom;
  let moved = false;
  if (keysDown.has("w") || keysDown.has("arrowup")) {
    camera.y -= speed;
    moved = true;
  }
  if (keysDown.has("s") || keysDown.has("arrowdown")) {
    camera.y += speed;
    moved = true;
  }
  if (keysDown.has("a") || keysDown.has("arrowleft")) {
    camera.x -= speed;
    moved = true;
  }
  if (keysDown.has("d") || keysDown.has("arrowright")) {
    camera.x += speed;
    moved = true;
  }
  if (moved) resolveAndUpdateAmbience(false);
  requestAnimationFrame(updateKeyPan);
}
updateKeyPan();

function rotateTile(dir) {
  const tile =
    selectedStorageIndex >= 0
      ? storage[selectedStorageIndex]
      : hand[selectedHandIndex];
  if (!tile || tile.isOverlay) return;
  tile.rotation = (((tile.rotation + dir) % 6) + 6) % 6;
  renderer.assignSpriteKeys(tile);
  renderHand();
  if (selectedStorageIndex >= 0) renderStoragePanel();
}

// ===== MENU =====
const menuBtn = document.getElementById("menu-btn");
const menuDropdown = document.getElementById("menu-dropdown");
menuBtn.addEventListener("mousedown", (e) => {
  e.stopPropagation();
});
menuBtn.addEventListener("mouseup", (e) => {
  e.stopPropagation();
  audio.playSfx("ui_click");
  menuDropdown.classList.toggle("visible");
});
menuBtn.addEventListener("click", (e) => {
  e.stopPropagation();
});
menuDropdown.addEventListener("mousedown", (e) => e.stopPropagation());
menuDropdown.addEventListener("mouseup", (e) => e.stopPropagation());
menuDropdown.addEventListener("click", (e) => e.stopPropagation());
document.addEventListener("mousedown", () => {
  menuDropdown.classList.remove("visible");
});

// ===== AUDIO VOLUME CONTROLS =====
const volMaster = document.getElementById("vol-master");
const volMusic = document.getElementById("vol-music");
const volSfx = document.getElementById("vol-sfx");
const volAmbience = document.getElementById("vol-ambience");
const ambienceToggle = document.getElementById("ambience-toggle");
const muteToggle = document.getElementById("mute-toggle");

// Init slider positions from stored volumes
volMaster.value = Math.round(audio.getVolume("master") * 100);
volMusic.value = Math.round(audio.getVolume("music") * 100);
volSfx.value = Math.round(audio.getVolume("sfx") * 100);
volAmbience.value = Math.round(audio.getVolume("ambience") * 100);
ambienceToggle.checked = ambienceManager.enabled;
muteToggle.checked = audio.isMuted();

volMaster.addEventListener("input", () =>
  audio.setVolume("master", volMaster.value / 100)
);
volMusic.addEventListener("input", () =>
  audio.setVolume("music", volMusic.value / 100)
);
volSfx.addEventListener("input", () =>
  audio.setVolume("sfx", volSfx.value / 100)
);
volAmbience.addEventListener("input", () => {
  audio.setVolume("ambience", volAmbience.value / 100);
  ambienceManager.onSettingsChanged();
});
ambienceToggle.addEventListener("change", () => {
  ambienceManager.setEnabled(ambienceToggle.checked);
  if (ambienceToggle.checked) resolveAndUpdateAmbience(true);
});
const weatherToggle = document.getElementById("weather-toggle");
weatherToggle.checked = weatherState.enabled;
weatherToggle.addEventListener("change", () => {
  weatherState.setEnabled(weatherToggle.checked);
});
muteToggle.addEventListener("change", () => audio.setMuted(muteToggle.checked));

function showResetModal() {
  menuDropdown.classList.remove("visible");
  document.getElementById("confirm-modal").classList.add("visible");
  audio.playSfx("ui_popup_open");
}
document.getElementById("reset-btn").addEventListener("click", showResetModal);
document.getElementById("confirm-yes").addEventListener("click", () => {
  audio.playSfx("ui_popup_close");
  localStorage.removeItem("hexaworlds_save");
  tiles = {};
  initResources();
  score = { stars: 0 };
  camera = { x: 0, y: 0, zoom: 1 };
  hand = [];
  selectedHandIndex = 0;
  tileIdCounter = 0;
  animations = [];
  placementAnims = [];
  pendingUnlockNotifs = [];
  overlayIdCounter = 0;
  rerollChainCount = 0;
  storageCapacity = 0;
  storage = [null, null, null];
  demolishMode = false;
  selectedStorageIndex = -1;
  document.getElementById("demolish-btn").classList.remove("active");
  canvas.classList.remove("demolish-cursor");
  _abilityUnlockTracked = { preview: false, reroll: false, storage: false };
  Analytics.sessionStarted = false;
  initOverlayState();
  clearAllRenderMeta();
  initGame();
  buildAllRenderMeta(tiles, spriteIndex?.tileTransitionKeys ?? null);
  questManager.reset();
  questUI.refresh(questManager.state.active);
  document.getElementById("confirm-modal").classList.remove("visible");
});
document.getElementById("confirm-no").addEventListener("click", () => {
  audio.playSfx("ui_popup_close");
  document.getElementById("confirm-modal").classList.remove("visible");
});

// ===== GALLERY =====
let galleryRenderedCount = 0;
const GALLERY_BATCH = 30;

function openGallery() {
  menuDropdown.classList.remove("visible");
  galleryRenderedCount = 0;
  document.getElementById("gallery-scroll").innerHTML = "";
  document.getElementById("gallery-modal").classList.add("visible");
  audio.playSfx("ui_popup_open");
  renderGalleryBatch();
}

function renderGalleryBatch() {
  if (!spriteIndex) return;
  const allKeys = spriteIndex.overlayKeysAll;
  const end = Math.min(galleryRenderedCount + GALLERY_BATCH, allKeys.length);
  const container = document.getElementById("gallery-scroll");
  for (let i = galleryRenderedCount; i < end; i++) {
    const key = allKeys[i];
    const item = document.createElement("div");
    item.className = "gallery-item";
    if (!overlayUnlocks.unlocked.includes(key)) {
      item.classList.add("locked");
    }
    const c = document.createElement("canvas");
    c.width = 72;
    c.height = 72;
    item.appendChild(c);
    const cctx = c.getContext("2d");
    if (spriteAtlas && spriteAtlas.has(key)) {
      const scale = 72 / 30;
      cctx.save();
      cctx.scale(scale, scale);
      spriteAtlas.draw(cctx, key, 0, SPRITE_OFFSET_Y);
      cctx.restore();
    }
    container.appendChild(item);
  }
  galleryRenderedCount = end;
}

document.getElementById("gallery-btn").addEventListener("click", openGallery);
document.getElementById("gallery-close").addEventListener("click", () => {
  audio.playSfx("ui_popup_close");
  document.getElementById("gallery-modal").classList.remove("visible");
});
document.getElementById("gallery-scroll").addEventListener("scroll", (e) => {
  const el = e.target;
  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
    renderGalleryBatch();
  }
});

// ===== FX GUIDE =====
document.getElementById("fx-guide-btn").addEventListener("click", () => {
  menuDropdown.classList.remove("visible");
  document.getElementById("fx-guide-modal").classList.add("visible");
  audio.playSfx("ui_popup_open");
});
document.getElementById("fx-guide-close").addEventListener("click", () => {
  audio.playSfx("ui_popup_close");
  document.getElementById("fx-guide-modal").classList.remove("visible");
});
const fxGuideModal = document.getElementById("fx-guide-modal");
fxGuideModal.addEventListener("mousedown", (e) => e.stopPropagation());
fxGuideModal.addEventListener("mouseup", (e) => e.stopPropagation());
fxGuideModal.addEventListener("click", (e) => e.stopPropagation());

// ===== SAVE/LOAD =====
function saveGame() {
  const state = {
    tiles: Object.values(tiles),
    resources,
    score,
    camera,
    hand,
    selectedHandIndex,
    tileIdCounter,
    overlayUnlocks,
    rerollChainCount,
    storageCapacity,
    storage,
  };
  localStorage.setItem("hexaworlds_save", JSON.stringify(state));
}

function loadGame() {
  const data = localStorage.getItem("hexaworlds_save");
  if (!data) return false;
  try {
    const state = JSON.parse(data);
    // Validate tile types
    const validTypes = new Set(TILE_TYPES);
    for (const t of state.tiles) {
      if (t.sides.some((s) => !validTypes.has(s))) {
        console.warn("Save contains unknown tile types, starting fresh");
        localStorage.removeItem("hexaworlds_save");
        return false;
      }
    }
    if (state.hand) {
      for (const t of state.hand) {
        if (t.isOverlay) continue;
        if (t.sides.some((s) => !validTypes.has(s))) {
          console.warn(
            "Save contains unknown tile types in hand, starting fresh"
          );
          localStorage.removeItem("hexaworlds_save");
          return false;
        }
      }
    }
    tiles = {};
    for (const t of state.tiles) tiles[hexKey(t.coord.q, t.coord.r)] = t;
    resources = state.resources || {};
    // Ensure all new resource keys exist, drop old ones
    const newRes = {};
    for (const res of RESOURCE_TYPES) {
      newRes[res] = resources[res] || 0;
    }
    resources = newRes;
    score = state.score;
    camera = state.camera || { x: 0, y: 0, zoom: 1 };
    hand = state.hand || [];
    selectedHandIndex = state.selectedHandIndex || 0;
    tileIdCounter = state.tileIdCounter || Object.keys(tiles).length;
    if (state.overlayUnlocks) {
      overlayUnlocks = state.overlayUnlocks;
    }
    // Load new ability state with backward compat
    rerollChainCount = state.rerollChainCount || 0;
    storageCapacity = state.storageCapacity || 0;
    storage = state.storage || [null, null, null];
    // Ensure storage array is always length 3
    while (storage.length < 3) storage.push(null);
    // Derive storage unlock from stars
    if (isStorageUnlocked() && storageCapacity < 1) {
      storageCapacity = 1;
    }
    return true;
  } catch (e) {
    return false;
  }
}

// ===== ATLAS LOADING =====
async function loadAtlas() {
  try {
    const atlas = await SpriteAtlas.load(tilesImageUrl, tilesJson);
    spriteAtlas = atlas;
    spriteIndex = buildSpriteIndex(atlas);
    renderer.spriteAtlas = atlas;
    renderer.spriteIndex = spriteIndex;
  } catch (e) {
    console.warn("Atlas load failed, using procedural fallback:", e);
  }
}

// ===== INIT =====
function initGame() {
  if (!loadGame()) {
    const firstTile = generateContiguousTileFallback();
    firstTile.coord = { q: 0, r: 0 };
    firstTile.placed = true;
    firstTile.placedAtMs = performance.now();
    tiles[hexKey(0, 0)] = firstTile;
    renderer.assignSpriteKeys(firstTile);
  }
  // Assign sprite keys to any loaded tiles that don't have them
  for (const tile of Object.values(tiles)) {
    if (!tile.baseSpriteKey) {
      renderer.assignSpriteKeys(tile);
    }
  }
  // Initialize overlay state if not loaded from save
  if (
    overlayUnlocks.locked.length === 0 &&
    overlayUnlocks.unlocked.length === 0
  ) {
    initOverlayState();
  }
  syncRendererState();
  fillHand();
  updateHUD();
  renderStoragePanel();
  initAbilityUnlockTracking();
  requestAnimationFrame(render);
}

// Display build number
document.getElementById("build-number").textContent = `Build #${BUILD_NUMBER}`;

// ===== ANALYTICS INIT =====
Analytics.init({ debug: new URLSearchParams(location.search).has("debugAnalytics") });
const _playerId = getOrCreatePlayerId();
setGaUserId(_playerId);

// ===== QUESTS v2 =====

/** Build the world info object for quest generation and progress. */
function buildQuestWorldInfo() {
  const tileValues = Object.values(tiles).filter(t => t.placed);
  const boardTerrainCounts = new Map();
  let boardOverlayCount = 0;
  for (const t of tileValues) {
    const type = getMajorityType(t.sides);
    boardTerrainCounts.set(type, (boardTerrainCounts.get(type) || 0) + 1);
    if (t.overlaySpriteKey) boardOverlayCount++;
  }
  const handTerrainTypes = new Set();
  for (const h of hand) {
    if (!h.isOverlay) handTerrainTypes.add(getMajorityType(h.sides));
  }
  const exposedCounts = getExposedTypeCounts();
  const exposedTopTypes = getTopExposed(exposedCounts, 3);
  const unlockedFamilies = new Set(overlayUnlocks.unlocked.map(k => getOverlayFamily(k)));

  return {
    totalTiles: tileValues.length,
    tiles,
    handTerrainTypes,
    exposedTopTypes,
    boardTerrainCounts,
    boardOverlayCount,
    unlockedFamilies,
    score,
    // Functions needed by progress updater
    _fns: { tiles, getMajorityType, getOverlayFamily },
  };
}

const questManager = new QuestManager();
questManager.getWorldInfo = buildQuestWorldInfo;

questManager.onStarsReward = (n) => {
  score.stars += n;
  overlayUnlocks.starsSinceLastUnlock += n;
  checkOverlayUnlock();
  checkAbilityUnlocks();
  // Show floating reward text near centre of screen
  const px = { x: window.innerWidth / 2, y: window.innerHeight / 2 - 60 };
  animations.push({
    x: px.x, y: px.y,
    text: `⭐ +${n}  Quest!`,
    startTime: performance.now(),
    duration: 1400,
    isScore: true,
  });
  updateHUD();
};

questManager.onRegularOverlayReward = () => {
  const pick = unlockOverlayFromPool('regular');
  if (pick) {
    pendingUnlockNotifs.push(pick);
    Analytics.track("overlay_unlock", {
      build: String(BUILD_NUMBER),
      source: "quest_creative",
      unlocked_total: overlayUnlocks.unlocked.length,
      locked_remaining: overlayUnlocks.locked.length,
    });
    audio.playSfx("overlay_unlock");
    updateHUD();
    showNextUnlockNotif();
  }
  return pick;
};

questManager.onSpecialOverlayReward = () => {
  const pick = unlockOverlayFromPool('special');
  if (pick) {
    pendingUnlockNotifs.push(pick);
    Analytics.track("overlay_unlock", {
      build: String(BUILD_NUMBER),
      source: "quest_special",
      unlocked_total: overlayUnlocks.unlocked.length,
      locked_remaining: overlayUnlocks.locked.length,
    });
    audio.playSfx("overlay_unlock");
    updateHUD();
    showNextUnlockNotif();
  }
  return pick;
};

questManager.onQuestComplete = (tier, quest) => {
  questUI.showCompletion(tier, quest);
  spawnConfetti();
};

questManager.onProgress = (tier, quest) => {
  questUI.updateProgress(tier, quest);
};

questManager.onQuestNew = (tier, quest) => {
  questUI.setQuest(tier, quest);
};

const _questPanelEl = document.getElementById("quest-panel");
const questUI = new QuestUI(_questPanelEl);

let resolveWorldReady;
const worldReadyPromise = new Promise((resolve) => {
  resolveWorldReady = resolve;
});
mountSplashScreen(worldReadyPromise);

// Load atlas then init game
loadAtlas().then(() => {
  initGame();
  buildAllRenderMeta(tiles, spriteIndex?.tileTransitionKeys ?? null);
  questManager.init();
  questUI.refresh(questManager.state.active);
  resolveWorldReady();
});
